/* eslint-disable-next-line simple-import-sort/imports */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { HydratableServicePointProfile } from '@/services/configuration/servicePoint/profileHydration';

import { hydrateSnapshot } from '@/app/store/stateSnapshot/hydrateSnapshotAction';
import { logout } from '@/features/auth/authSlice';
import {
    clearCachedServicePointId,
    getCachedServicePointId,
    setCachedServicePointId,
} from '@/features/servicePointSelection/utils/servicePointUtils';

/* THIS STORE USED FOR MANAGING SERVICE POINT PROFILE DATA */
/* SERVICE POINT PROFILE FORMAT
1. srvPntFcId: Service Point Facility ID
2. srvACd: Service Area Code
3. srvPntNm: Service Point Name
4. ctryCd: Country Code
5. ctyNm: City Name
6. addrLn1: Address Line 1
7. addrLn2: Address Line 2
8. addrLn3: Address Line 3
9. finalizeAccNo: Finalize Account Number
10. srvPrflCfgKey: Service Profile Configuration Keys (array of objects with 'cd' and '$' properties)
10.1. custOwnPkgAlw: Customer Own Package Allowed (Y/N)
10.2. shpInspcRqr: Shipment Inspection Required (Y/N)
10.3. multiPcsShpAlw: Multi-Piece Shipment Allowed (Y/N)
10.4. esecureVrfctnEnbld: eSecure Verification Enabled (Y/N)
10.5. custCntVrfctnEnbld: Customer Contact Verification Enabled (Y/N)
10.6. cnstMngmtPltfrmmEnbld: Consignment Management Platform Enabled (Y/N)
10.7. cmpCnstMth: CMP Consent Methods (array of strings, e.g., ["QR", "EMAIL"])
      Note: This references codes that should match state.cache.cmpConsentMethod entries
      Cache format: [{ cd: "QR", lbl: null, dsc: "QR Code", prntCd: null }, ...]
10.8. cnstCountdownDr: Consent Countdown Duration in seconds (string, default: "60")
10.9. cnstMaxResendAtmpt: Maximum Consent Resend Attempts (string, default: "3")
11. srvPrflPayMth: Service Profile Payment Methods (array of strings)
12. srvPrflProd: Service Profile Products (array of strings)
13. srvPrflSrv: Service Profile Services (array of strings)
14. srvPrflPkg: Service Profile Packages (array of strings)
15. srvPrflTrf: Service Profile Tariffs (array of strings)
16. srvPntPrflCd: Service Point Profile Code
*/

export interface ServicePointProfile extends HydratableServicePointProfile {
    srvACd?: string;
    srvPrflPkg?: string[];
    srvPrflTrf?: Array<{ dsplNm: string; trfCd: string; st?: string }>;
    srvPrflSrv?: Array<{ cd: string; defSelInd: string }>;
    srvPrflCfgKey?: Array<{ cd: string; $: string }>;
}

export interface ServicePointProfileState {
    profile: ServicePointProfile | null;
}

/**
 * Seed the slice from localStorage on every page load so that a hard
 * refresh preserves the SP id.  Only srvPntFcId is stored — the full
 * profile (name, tariffs, etc.) is loaded by servicePointProfileService
 * in MainLayout using this id as the preferred key.
 */
export function buildInitialState(): ServicePointProfileState {
    const cachedId = getCachedServicePointId();
    return { profile: cachedId ? { srvPntFcId: cachedId } : null };
}

const initialState: ServicePointProfileState = buildInitialState();

const servicePointProfileSlice = createSlice({
    name: 'servicePointProfile',
    initialState,
    reducers: {
        setServicePointProfile(state, action: PayloadAction<ServicePointProfile>) {
            state.profile = action.payload;
            if (action.payload?.srvPntFcId) {
                setCachedServicePointId(action.payload.srvPntFcId);
            } else {
                clearCachedServicePointId();
            }
        },
        clearServicePointProfile(state) {
            state.profile = null;
            clearCachedServicePointId();
        },
        resetServicePointProfileState(state) {
            state.profile = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(logout, (state) => {
            // Keep the last selected service point in localStorage so the next
            // session can restore it when the user selects the same country.
            state.profile = null;
        });
        builder.addCase(hydrateSnapshot, (state, action) => {
            const persisted = action.payload.servicePointProfile;
            if (!persisted) return;

            const cachedId = getCachedServicePointId();
            if (cachedId && persisted.profile?.srvPntFcId !== cachedId) {
                // The localStorage id is updated synchronously on service-point
                // changes, while the encrypted shared snapshot is asynchronous.
                // Keep an already-loaded matching profile; otherwise restore the cached id.
                state.profile =
                    state.profile?.srvPntFcId === cachedId
                        ? state.profile
                        : { srvPntFcId: cachedId };
                return;
            }

            state.profile = persisted.profile ?? null;
        });
    },
});

export const { setServicePointProfile, clearServicePointProfile, resetServicePointProfileState } =
    servicePointProfileSlice.actions;
export default servicePointProfileSlice.reducer;

/**
 * Returns true when the active service point profile explicitly enables Last Mile
 * (lstMileEnbld = "Y").  Returns false when the key is absent, set to "N", or
 * when no profile has been loaded yet.
 */
export const selectIsLastMileEnabled = (state: {
    servicePointProfile: ServicePointProfileState;
}): boolean => {
    const cfgKeys = state.servicePointProfile.profile?.srvPrflCfgKey;
    if (!Array.isArray(cfgKeys)) return false;
    return cfgKeys.find(({ cd }) => cd === 'lstMileEnbld')?.$ === 'Y';
};

/**
 * Returns true when the active service point profile explicitly enables eSecure verification
 * (esecureVrfctnEnbld = "Y"). Returns false when the key is absent, set to "N", or
 * when no profile has been loaded yet.
 */
export const selectIsESecureVerificationEnabled = (state: {
    servicePointProfile: ServicePointProfileState;
}): boolean => {
    const cfgKeys = state.servicePointProfile.profile?.srvPrflCfgKey;
    if (!Array.isArray(cfgKeys)) return false;
    return cfgKeys.find(({ cd }) => cd === 'esecureVrfctnEnbld')?.$ === 'Y';
};
